/**
 * State + business logic for the NUTRITION create-client wizard.
 *
 * A deliberate subset of usePatientForm: the classic wizard is a therapist's
 * flow. Everything a nutritionist does not use at intake (couples, parents,
 * files, admin & schedule, the clinical checklist) is dropped here and stays
 * editable later from the patient profile — see DEVELOPER_GUIDE §27.5 for the
 * field-by-field audit.
 *
 * Creation is the same path as everywhere else: createPatient (mutation) →
 * optional first measurement → personal-page link.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useRegisterUnsavedChanges } from "../../../../contexts/UnsavedChangesContext";
import { useToast } from "../../../../contexts/ToastContext";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectClinicProfile,
  selectIsAdmin,
  selectIsSecretary,
  selectHasPermission,
  selectEnglishCommunicationEnabled,
} from "../../../../store/slices/authSlice";
import { isAssignableTherapist } from "../../../../config/permissionsRegistry";
import { useGetClinicTherapistsQuery } from "../../../../store/services/therapistApi";
import { useCreatePatientMutation } from "../../../../store/services/patientsApi";
import usePlanPermissions from "../../../../hooks/usePlanPermissions";
import {
  createMeasurement,
  hasNutritionGoals,
  MEASUREMENT_FIELDS,
} from "../../../../firebase";
import {
  DEFAULT_GOAL_TYPE,
  calcAgeFromBirthDate,
  calcBmi,
  calcBmr,
  calcBodyFat,
  calcTdee,
  suggestDailyGoals,
} from "../../../../utils/nutritionCalc";

export const NUTRITION_STEPS = {
  CLIENT: "client",
  BODY: "body",
  GOALS: "goals",
  COACHING: "coaching",
  FINISH: "finish",
};

/** The order the coach walks; also the "המשך" order. */
export const NUTRITION_STEP_ORDER = [
  NUTRITION_STEPS.CLIENT,
  NUTRITION_STEPS.BODY,
  NUTRITION_STEPS.GOALS,
  NUTRITION_STEPS.COACHING,
  NUTRITION_STEPS.FINISH,
];

/** Client-facing switches — all ON by default (createPatient enforces this too). */
export const DEFAULT_NUTRITION = {
  selfLoggingEnabled: true,
  aiEstimateEnabled: true,
  selfMeasurementsEnabled: true,
  showGoalsToClient: true,
  activityLevel: null,
  goalType: DEFAULT_GOAL_TYPE,
  goals: { dietPlan: "", calories: null, protein: null, carbohydrates: null, fat: null },
  measurementSchedule: { enabled: false, frequency: "weekly", weekday: 0, time: "08:00" },
};

const initialFormData = {
  // Step 1 — פרטי לקוח
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  referralReason: "",
  communicationLanguage: "he",

  // Step 2 — פרטים אישיים ומדדים
  birthDate: "",
  gender: "",
  clinicalNotes: "", // רקע רפואי — one free-text field
  initialMeasurement: {}, // → the first measurements document, never on the patient doc
  // Coach-typed BMI / BMR that beat the formula on the first measurement
  // (Round 3 / Phase 20). Body fat is overridden through initialMeasurement.fatPercentage.
  metricOverrides: { bmi: null, bmr: null },

  // Steps 3–4 — patient.nutrition
  nutrition: DEFAULT_NUTRITION,
};

const isFilled = (value) => value !== undefined && value !== null && value !== "";

function useNutritionPatientForm() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const clinicProfile = useSelector(selectClinicProfile);
  const isAdmin = useSelector(selectIsAdmin);
  const englishCommunicationEnabled = useSelector(selectEnglishCommunicationEnabled);

  // Admin / "patients.createForOthers" holders in a multi-therapist clinic can
  // assign the client to a colleague. Secretaries must pick (self excluded).
  const isSecretary = useSelector(selectIsSecretary);
  const canCreateForOthers = useSelector(selectHasPermission("patients.createForOthers"));
  const canAssignToOtherTherapist =
    (isAdmin || canCreateForOthers) && !clinicProfile?.isSoloClinic;
  const therapistRequired = isSecretary;
  // Secretary without the right to assign a coach ⇒ nobody to create for.
  const creationBlocked = isSecretary && !canAssignToOtherTherapist;
  const [selectedTherapistId, setSelectedTherapistId] = useState(
    isSecretary ? "" : therapistId
  );

  const { data: clinicTherapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId || !canAssignToOtherTherapist }
  );
  const activeTherapists = useMemo(
    () =>
      clinicTherapists.filter(
        (t) => isAssignableTherapist(t) && (!isSecretary || t.uid !== therapistId)
      ),
    [clinicTherapists, isSecretary, therapistId]
  );

  useEffect(() => {
    if (therapistId && !selectedTherapistId && !isSecretary) {
      setSelectedTherapistId(therapistId);
    }
  }, [therapistId, selectedTherapistId, isSecretary]);

  const effectiveTherapistId = canAssignToOtherTherapist ? selectedTherapistId : therapistId;
  const effectiveTherapistName = useMemo(() => {
    if (!canAssignToOtherTherapist) return therapistName;
    const selected = activeTherapists.find((t) => t.uid === selectedTherapistId);
    return selected ? `${selected.firstName} ${selected.lastName}` : therapistName;
  }, [canAssignToOtherTherapist, selectedTherapistId, activeTherapists, therapistName]);

  const { checkPatient, isChecking, PlanLimitModalComponent } = usePlanPermissions();
  const [createPatient, { isLoading }] = useCreatePatientMutation();

  // ===== Steps =====
  const [activeStep, setActiveStep] = useState(NUTRITION_STEPS.CLIENT);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  // Goals overlay: lifted by "אני רוצה להגדיר"; "דלג/י" is explicit and remembered
  const [goalsRevealed, setGoalsRevealed] = useState(false);
  const [goalsSkipped, setGoalsSkipped] = useState(false);

  // After creation
  const [createdPatient, setCreatedPatient] = useState(null);

  const isDirty = useMemo(
    () => !createdPatient && JSON.stringify(formData) !== JSON.stringify(initialFormData),
    [formData, createdPatient]
  );
  useRegisterUnsavedChanges(isDirty);

  // ===== Field handlers =====
  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
  }, []);

  const setNutrition = useCallback(
    (patch) => setFormData((prev) => ({ ...prev, nutrition: { ...prev.nutrition, ...patch } })),
    []
  );

  const setGoal = useCallback(
    (key, value) =>
      setFormData((prev) => ({
        ...prev,
        nutrition: { ...prev.nutrition, goals: { ...prev.nutrition.goals, [key]: value } },
      })),
    []
  );

  const setSchedule = useCallback(
    (key, value) =>
      setFormData((prev) => ({
        ...prev,
        nutrition: {
          ...prev.nutrition,
          measurementSchedule: { ...prev.nutrition.measurementSchedule, [key]: value },
        },
      })),
    []
  );

  const setMeasurementField = useCallback(
    (field, value) =>
      setFormData((prev) => ({
        ...prev,
        initialMeasurement: { ...prev.initialMeasurement, [field]: value },
      })),
    []
  );

  const setMetricOverride = useCallback(
    (key, value) =>
      setFormData((prev) => ({
        ...prev,
        metricOverrides: { ...prev.metricOverrides, [key]: value },
      })),
    []
  );

  const formatPhone = (value) => value.replace(/\D/g, "").slice(0, 10);

  // ===== Live body metrics (step 2) =====
  // Never written from here — createMeasurement derives and snapshots them
  // server-side. This is the coach's live preview, explained inline.
  const bodyMetrics = useMemo(() => {
    const m = formData.initialMeasurement || {};
    const age = calcAgeFromBirthDate(formData.birthDate);
    const gender = formData.gender || null;
    const weight = Number(m.weight) || null;
    const height = Number(m.height) || null;

    const computedBmi = calcBmi({ weight, height });
    const computedBmr = calcBmr({ weight, height, age, gender });
    const overrides = formData.metricOverrides || {};
    const bmiManual = isFilled(overrides.bmi) ? Number(overrides.bmi) : null;
    const bmrManual = isFilled(overrides.bmr) ? Number(overrides.bmr) : null;
    const bmi = bmiManual ?? computedBmi;
    const bmr = bmrManual ?? computedBmr;

    const measuredFat = isFilled(m.fatPercentage) ? Number(m.fatPercentage) : null;
    const estimatedFat =
      measuredFat === null
        ? calcBodyFat({ height, abdomen: m.waist, neck: m.neck }, gender)
        : null;

    return {
      age,
      gender,
      bmi,
      bmr,
      computedBmi,
      computedBmr,
      bmiIsManual: bmiManual !== null,
      bmrIsManual: bmrManual !== null,
      tdee: bmr ? calcTdee(bmr, formData.nutrition?.activityLevel) : null,
      fatPercentage: measuredFat ?? (estimatedFat > 0 ? estimatedFat : null),
      fatIsEstimated: measuredFat === null && estimatedFat > 0,
      fatIsManual: measuredFat !== null,
      weight,
      hasWeight: !!weight,
      hasHeight: !!height,
    };
  }, [
    formData.initialMeasurement,
    formData.metricOverrides,
    formData.birthDate,
    formData.gender,
    formData.nutrition,
  ]);

  // Suggested daily goals — helper text only, never auto-filled or persisted.
  const goalSuggestions = useMemo(
    () =>
      suggestDailyGoals({
        bmr: bodyMetrics.bmr,
        activityLevel: formData.nutrition?.activityLevel,
        weight: bodyMetrics.weight,
        goalType: formData.nutrition?.goalType,
        dietPlan: formData.nutrition?.goals?.dietPlan,
        caloriesEntered: formData.nutrition?.goals?.calories,
      }),
    [bodyMetrics, formData.nutrition]
  );

  const hasInitialMeasurement = useMemo(
    () => Object.keys(MEASUREMENT_FIELDS).some((f) => isFilled(formData.initialMeasurement?.[f])),
    [formData.initialMeasurement]
  );

  const goalsAreSet = hasNutritionGoals(formData.nutrition?.goals);

  // ===== Validation (step 1 only — everything else is optional) =====
  const validateClientStep = () => {
    const next = {};
    if (!formData.firstName.trim()) next.firstName = "שדה חובה";
    if (!formData.lastName.trim()) next.lastName = "שדה חובה";
    if (!formData.phone.trim()) next.phone = "שדה חובה";
    else if (formData.phone.length !== 10) next.phone = "מספר טלפון צריך להיות 10 ספרות";
    if (therapistRequired && !selectedTherapistId) {
      next.therapistId = "יש לבחור את המאמן/ת של הלקוח/ה";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const canSave =
    formData.firstName.trim() &&
    formData.lastName.trim() &&
    formData.phone.length === 10 &&
    (!therapistRequired || !!selectedTherapistId);

  // ===== Navigation =====
  const goNext = () => {
    const index = NUTRITION_STEP_ORDER.indexOf(activeStep);
    if (activeStep === NUTRITION_STEPS.CLIENT && !validateClientStep()) return;
    const next = NUTRITION_STEP_ORDER[index + 1];
    if (next && next !== NUTRITION_STEPS.FINISH) setActiveStep(next);
  };

  const goBack = () => {
    const index = NUTRITION_STEP_ORDER.indexOf(activeStep);
    if (index > 0) setActiveStep(NUTRITION_STEP_ORDER[index - 1]);
  };

  const goToStep = (step) => {
    if (step === NUTRITION_STEPS.FINISH && !createdPatient) return;
    setActiveStep(step);
  };

  // ===== Create =====
  const handleSubmit = async () => {
    if (!validateClientStep()) {
      setActiveStep(NUTRITION_STEPS.CLIENT);
      return;
    }
    if (!clinicId || !effectiveTherapistId) {
      console.error("[CK-2620] Missing required auth data:", {
        errorCode: "CK-2620",
        errorMessage: "Missing clinicId or therapistId - user profile may not be loaded",
        clinicId,
        therapistId: effectiveTherapistId,
        timestamp: new Date().toISOString(),
      });
      setErrors({ submit: "שגיאה בטעינת פרטי המשתמש. נסה/י לרענן את הדף." });
      return;
    }

    const canProceed = await checkPatient();
    if (!canProceed) return; // the hook shows its own modal

    const { initialMeasurement, metricOverrides, ...patientFields } = formData;

    try {
      const result = await createPatient({
        clinicId,
        therapistId: effectiveTherapistId,
        therapistName: effectiveTherapistName,
        enableAiPatientSummary: clinicProfile?.enableAiPatientSummary === true,
        patientData: {
          ...patientFields,
          status: "active",
          isDraft: false,
        },
      }).unwrap();

      // Opening measurement — best effort, exactly like the classic flow:
      // a lost measurement must never present itself as a failed creation.
      if (hasInitialMeasurement) {
        try {
          await createMeasurement(
            clinicId,
            result.id,
            { ...initialMeasurement, metricOverrides, source: "coach" },
            { therapistId: effectiveTherapistId, therapistName: effectiveTherapistName }
          );
        } catch (measurementError) {
          console.error("[CK-2610] Error saving the first measurement:", {
            errorCode: "CK-2610",
            errorMessage: measurementError.message,
            clinicId,
            patientId: result.id,
            timestamp: new Date().toISOString(),
          });
          showError("הלקוח/ה נוצר/ה, אך שמירת המדידה הראשונה נכשלה");
        }
      }

      setCreatedPatient(result);
      setActiveStep(NUTRITION_STEPS.FINISH);
      showSuccess("הלקוח/ה נוצר/ה בהצלחה");
    } catch (error) {
      console.error("[CK-2001] Error creating patient:", {
        errorCode: "CK-2001",
        errorMessage: error.message,
        clinicId,
        therapistId: effectiveTherapistId,
        timestamp: new Date().toISOString(),
      });
      const isAuthError =
        error.message?.includes("auth/") || error.code === "auth/user-token-expired";
      showError(isAuthError ? "פג תוקף החיבור. אנא התחבר/י מחדש." : "שגיאה ביצירת הלקוח/ה");
    }
  };

  // ===== Close =====
  const handleSafeClose = () => {
    if (createdPatient) {
      navigate(`/dashboard/nutrition/${createdPatient.id}`);
      return;
    }
    if (isDirty) setShowCloseWarning(true);
    else navigate("/dashboard/patients");
  };

  const handleDiscardChanges = () => navigate("/dashboard/patients");

  return {
    // steps
    activeStep,
    goToStep,
    goNext,
    goBack,
    // form
    formData,
    errors,
    handleChange,
    setNutrition,
    setGoal,
    setSchedule,
    setMeasurementField,
    setMetricOverride,
    formatPhone,
    // derived
    bodyMetrics,
    goalSuggestions,
    hasInitialMeasurement,
    goalsAreSet,
    goalsRevealed,
    setGoalsRevealed,
    goalsSkipped,
    setGoalsSkipped,
    canSave,
    // context
    englishCommunicationEnabled,
    canAssignToOtherTherapist,
    isSecretary,
    therapistRequired,
    creationBlocked,
    activeTherapists,
    selectedTherapistId,
    setSelectedTherapistId,
    therapistId,
    // after creation
    createdPatient,
    clinicId,
    // submit / close
    handleSubmit,
    handleSafeClose,
    handleDiscardChanges,
    showCloseWarning,
    setShowCloseWarning,
    isLoading,
    isChecking,
    saveDisabled: isLoading || !clinicId || !effectiveTherapistId,
    PlanLimitModalComponent,
  };
}

export default useNutritionPatientForm;
